import type { QueryClient } from '@tanstack/react-query'
import type { z } from 'zod'

import { apiClient, getData, getPage } from '@/api/client'
import { marketEndpoints } from '@/api/endpointCache'
import { lenientList } from '@/schemas/common'
import {
  closedTradeSchema,
  cycleMapSchema,
  levelBoardSchema,
  liveRunsSchema,
  momentumRowSchema,
  openTradeSchema,
  signalBoardSchema,
  signalDashboardSchema,
  signalPerformanceSchema,
  signalSessionsSchema,
  signalSetupSchema,
  signalTechnicalsSchema,
  signalsListMetaSchema,
  steadyRowSchema,
  swingRowSchema,
  trendRowSchema,
  type BreakoutsQuery,
  type ClosedTrade,
  type CycleMap,
  type CycleMapQuery,
  type HistoryQuery,
  type LevelBoard,
  type LiveRun,
  type MomentumRow,
  type OpenTrade,
  type SignalBoard,
  type SignalDashboard,
  type SignalPerformance,
  type SignalSetup,
  type SignalTechnicals,
  type SignalsList,
  type SignalsPage,
  type SignalsPaging,
  type SteadyQuery,
  type SteadyRow,
  type SwingRow,
  type TrendQuery,
  type VolatileQuery,
  type TrendRow,
} from '@/schemas/signals'

/**
 * The Signal Zone seam: fifteen `/api/v5/signals/…` reads in, app types out
 * (the chart's layers are `overlaysService`'s).
 *
 * Every read is JWT-only behind mm-be's `SharkSignalRequired` (Shark plus the
 * Signals add-on, enforced only when mm-be's subscription mode says so). A
 * refusal throws the 403 `SUBSCRIPTION_REQUIRED` as it comes; the screens
 * branch on that code and draw the locked state (owner decision D4).
 *
 * Bodies are read as `unknown` and parsed by `@/schemas/signals`, whose wire
 * schemas do the snake-to-camel mapping too: see that file for why the parse,
 * not a `Wire<>` mapper, is the boundary here. A `null` session date or search
 * text sends no parameter at all, so mm-be applies its own default.
 */

/**
 * A query object with its null members left out, as the typed `params.query`
 * wants them. The cast only restores the keys `Object.fromEntries` forgets.
 */
const present = <T extends Record<string, unknown>>(query: T) =>
  Object.fromEntries(Object.entries(query).filter(([, value]) => value !== null)) as {
    [K in keyof T as null extends T[K] ? never : K]: T[K]
  } & { [K in keyof T as null extends T[K] ? K : never]?: Exclude<T[K], null> }

/**
 * One server page. mm-be sends `page`, `page_size`, `total` and `total_pages`
 * but documents only `count` (B6), so each falls back to what was asked for,
 * or to what the rows themselves say.
 */
const toPage = <Row>(rows: Row[], rawMeta: unknown, asked: SignalsPaging): SignalsPage<Row> => {
  const meta = signalsListMetaSchema.parse(rawMeta)
  const pageSize = meta.page_size ?? asked.pageSize
  const total = meta.total ?? meta.count
  return {
    rows,
    page: meta.page ?? asked.page,
    pageSize,
    total,
    totalPages: meta.total_pages ?? Math.max(1, Math.ceil(total / pageSize)),
    date: meta.date ?? null,
  }
}

/** A list body's rows, row by row (`lenientList`). */
const rowsOf = <Row>(schema: z.ZodType<Row>, label: string, rows: unknown[]): Row[] =>
  lenientList(schema, label).parse(rows)

export const signalsService = {
  /** The computed sessions, newest first — what a session picker offers. */
  sessions: async (limit: number): Promise<string[]> => {
    const { rows } = await getPage<unknown>(() =>
      apiClient.GET('/api/v5/signals/sessions/', { params: { query: { limit } } }),
    )
    return signalSessionsSchema.parse(rows)
  },

  /** Counts and the market block for a session; null when it was not computed (a holiday). */
  dashboard: async (date: string | null): Promise<SignalDashboard | null> => {
    const data = await getData<unknown>(() =>
      apiClient.GET('/api/v5/signals/dashboard/', { params: { query: present({ date }) } }),
    )
    return signalDashboardSchema.nullable().parse(data)
  },

  /** The after-close run's boards for a session; null when that session had no signals. */
  daily: async (date: string | null): Promise<SignalBoard | null> => {
    const data = await getData<unknown>(() =>
      apiClient.GET('/api/v5/signals/daily/', { params: { query: present({ date }) } }),
    )
    return signalBoardSchema.nullable().parse(data)
  },

  /**
   * The intraday boards for the session in progress: every stock that
   * signalled at any run (`run` null), or one run's picture (`run` an id from
   * `liveRuns`). Null both before the first run and after a run that found
   * nothing — mm-be cannot tell the two apart yet (B9).
   */
  live: async (run: number | null): Promise<SignalBoard | null> => {
    const data = await getData<unknown>(() =>
      apiClient.GET('/api/v5/signals/live/', { params: { query: present({ run }) } }),
    )
    return signalBoardSchema.nullable().parse(data)
  },

  /** The session's live runs, oldest first: the run picker's options. */
  liveRuns: async (): Promise<LiveRun[]> => {
    const { rows } = await getPage<unknown>(() => apiClient.GET('/api/v5/signals/live/runs/'))
    return liveRunsSchema.parse(rows)
  },

  /** Every stock's trend checks for a session, A→Z, paged; `q` is a symbol prefix. */
  trend: async (query: TrendQuery): Promise<SignalsPage<TrendRow>> => {
    const { rows, meta } = await getPage<unknown>(() =>
      apiClient.GET('/api/v5/signals/trend/', {
        params: {
          query: present({
            date: query.date,
            q: query.q,
            page: query.page,
            page_size: query.pageSize,
          }),
        },
      }),
    )
    return toPage(rowsOf(trendRowSchema, 'trend', rows), meta, query)
  },

  /** One session's breakouts in one direction; null on a session with none (dev's default today). */
  breakouts: async (query: BreakoutsQuery): Promise<LevelBoard | null> => {
    const data = await getData<unknown>(() =>
      apiClient.GET('/api/v5/signals/breakouts/', {
        params: { query: present({ direction: query.direction, date: query.date }) },
      }),
    )
    return levelBoardSchema.nullable().parse(data)
  },

  /** Widest mean daily swing first, over the last 30 sessions. Takes no date. */
  volatile: async (query: VolatileQuery): Promise<SignalsPage<SwingRow>> => {
    const { rows, meta } = await getPage<unknown>(() =>
      apiClient.GET('/api/v5/signals/volatile/', {
        // `q` only reaches the wire once mm-be takes it (ask A1); the generated query type has none.
        params: {
          query: present({ q: query.q, page: query.page, page_size: query.pageSize }),
        },
      }),
    )
    return toPage(rowsOf(swingRowSchema, 'volatile', rows), meta, query)
  },

  /** Essentials › Steady: the session's tightest bands first. */
  steady: async (query: SteadyQuery): Promise<SignalsPage<SteadyRow>> => {
    const { rows, meta } = await getPage<unknown>(() =>
      apiClient.GET('/api/v5/signals/steady/', {
        params: {
          query: present({ date: query.date, page: query.page, page_size: query.pageSize }),
        },
      }),
    )
    return toPage(rowsOf(steadyRowSchema, 'steady', rows), meta, query)
  },

  /**
   * Essentials › Momentum: highest RSI first, on the current technicals. Takes
   * no date. Locally it answered a 500 when its legacy source table was absent
   * (B7), so a screen gives it a card-local failure, never the page's.
   */
  momentum: async (paging: SignalsPaging): Promise<SignalsPage<MomentumRow>> => {
    const { rows, meta } = await getPage<unknown>(() =>
      apiClient.GET('/api/v5/signals/momentum/', {
        params: { query: { page: paging.page, page_size: paging.pageSize } },
      }),
    )
    return toPage(rowsOf(momentumRowSchema, 'momentum', rows), meta, paging)
  },

  /**
   * Green signals still open, biggest move first — every one in one answer
   * (99 on dev), so a screen pages them itself. Takes no date.
   */
  openTrades: async (): Promise<SignalsList<OpenTrade>> => {
    const { rows, meta } = await getPage<unknown>(() =>
      apiClient.GET('/api/v5/signals/open-trades/'),
    )
    const parsed = rowsOf(openTradeSchema, 'open trade', rows)
    return { rows: parsed, count: signalsListMetaSchema.parse(meta).count }
  },

  /** Closed green → red pairs over ~2 years, latest exit first, paged; `q` is a symbol prefix. */
  history: async (query: HistoryQuery): Promise<SignalsPage<ClosedTrade>> => {
    const { rows, meta } = await getPage<unknown>(() =>
      apiClient.GET('/api/v5/signals/history/', {
        params: { query: present({ q: query.q, page: query.page, page_size: query.pageSize }) },
      }),
    )
    return toPage(rowsOf(closedTradeSchema, 'closed trade', rows), meta, query)
  },

  /**
   * One stock's every closed signal since it listed, replayed from Rs
   * 1,00,000, with the Signal page's twelve figures and any signal still
   * open. A symbol mm-be does not list throws its 404
   * `SIGNALS_SYMBOL_NOT_FOUND` as it comes; a listed stock with no signals
   * is an answer (no trades, null figures).
   */
  performance: async (symbol: string): Promise<SignalPerformance> => {
    const data = await getData<unknown>(() =>
      apiClient.GET('/api/v5/signals/stocks/{symbol}/performance/', {
        params: { path: { symbol } },
      }),
    )
    return signalPerformanceSchema.parse(data)
  },

  /**
   * One stock's oscillators, moving averages and monthly pivots, each tab
   * with its gauge. The same 404 as `performance` for a symbol mm-be does not
   * list; a listed stock with too little history is empty rows and no pivots.
   */
  technicals: async (symbol: string): Promise<SignalTechnicals> => {
    const data = await getData<unknown>(() =>
      apiClient.GET('/api/v5/signals/stocks/{symbol}/technicals/', {
        params: { path: { symbol } },
      }),
    )
    return signalTechnicalsSchema.parse(data)
  },

  /**
   * One stock's open setup, or `setup: null` when none is: most stocks have none. Same 404 as
   * `performance` for a symbol mm-be does not list, and the same plan gate (a 403 throws).
   */
  setup: async (symbol: string): Promise<SignalSetup> => {
    const data = await getData<unknown>(() =>
      apiClient.GET('/api/v5/signals/stocks/{symbol}/setup/', {
        params: { path: { symbol } },
      }),
    )
    return signalSetupSchema.parse(data)
  },

  /**
   * Every index and sector sub-index symbol, upper-cased: the keys of
   * `/market/indices/`, which lists each INDEX and SUB_INDEX mm-be prices
   * (`services/market/indices.py`). Signal rows carry them beside stocks with
   * no type to tell them apart (B15). Through the endpoint cache, so the
   * rail's own read of that list joins this one rather than repeating it.
   */
  indexSymbols: async (client?: QueryClient): Promise<string[]> => {
    const { rows } = await marketEndpoints(client).indices()
    return rows.map((row) => row.key.toUpperCase())
  },

  /** Average return per sector by Nepali month or fiscal quarter; null when nothing was computed. */
  cycleMap: async (query: CycleMapQuery): Promise<CycleMap | null> => {
    const data = await getData<unknown>(() =>
      apiClient.GET('/api/v5/signals/cycle-map/', {
        params: { query: { by: query.by, years: query.years } },
      }),
    )
    return cycleMapSchema.nullable().parse(data)
  },
}
